import * as THREE from 'three';

// Equirectangular projection helper
function p(lon: number, lat: number, W: number, H: number): [number, number] {
  return [(lon + 180) / 360 * W, (90 - lat) / 180 * H];
}

// Continent polygon data as flat [lon, lat, lon, lat, ...]
const POLYS: [string, number[]][] = [
  ['northAmerica', [
    -168,55, -170,60, -165,66, -155,62, -148,60, -138,58,
    -130,54, -126,50, -124,44, -124,38, -117,32, -112,22,
    -88,14, -83,9, -75,9, -68,10, -65,18, -74,22, -80,26,
    -83,30, -78,36, -74,40, -70,44, -63,46, -56,48, -55,52,
    -58,56, -65,60, -79,68, -97,72, -120,72, -148,70,
    -165,65, -168,55,
  ]],
  ['southAmerica', [
    -80,9, -77,11, -72,12, -62,15, -53,5, -50,2, -48,-2,
    -40,-6, -36,-10, -35,-18, -38,-23, -44,-30, -52,-34,
    -58,-38, -62,-42, -66,-46, -68,-52, -72,-56,
    -68,-50, -65,-44, -68,-36, -70,-30, -72,-24,
    -80,-18, -80,-10, -78,-4, -78,0, -78,4, -80,8, -80,9,
  ]],
  ['europe', [
    -10,36, 0,35, 10,35, 16,38, 22,37, 28,37, 36,40,
    38,42, 40,46, 38,48, 32,50, 26,54, 20,56, 16,58,
    14,62, 16,70, 28,72, 12,72, 6,62, 0,58, -4,54,
    -8,50, -10,46, -4,42, -8,36, -10,36,
  ]],
  ['africa', [
    -5,36, 10,37, 25,37, 34,31, 36,22, 42,15, 44,12,
    50,8, 44,6, 40,2, 36,-2, 36,-6, 38,-12, 38,-18,
    32,-24, 28,-28, 26,-32, 20,-36, 14,-34, 10,-28,
    6,-22, 4,-14, 2,-4, 0,2, -4,8, -10,14, -16,14,
    -18,16, -16,22, -8,30, -5,36,
  ]],
  ['asia', [
    28,38, 36,36, 40,36, 44,40, 50,42, 56,42,
    60,38, 64,38, 68,40, 74,38, 80,34, 84,28,
    88,22, 92,22, 98,18, 100,12, 104,4, 108,2,
    112,2, 116,4, 120,10, 124,14, 128,28, 130,34,
    132,38, 136,40, 140,44, 142,48, 138,52, 130,55,
    128,58, 120,60, 112,64, 104,68, 96,70, 86,72,
    76,72, 66,70, 56,68, 46,68, 36,65, 30,62,
    24,60, 22,58, 24,55, 26,52, 30,48, 34,46,
    36,42, 40,42, 44,40, 28,38,
  ]],
  ['arabia', [
    36,30, 38,28, 40,22, 44,18, 48,14, 52,12, 58,8,
    60,14, 62,20, 58,24, 56,26, 54,24, 50,22,
    46,20, 42,22, 38,26, 36,30,
  ]],
  ['india', [
    68,22, 72,22, 76,18, 80,12, 80,8, 78,6,
    76,8, 74,12, 72,16, 70,20, 68,22,
  ]],
  ['indochina', [
    100,22, 104,20, 108,16, 108,10, 104,4, 108,2,
    112,2, 118,8, 114,14, 108,20, 104,22, 100,22,
  ]],
  ['australia', [
    114,-22, 118,-16, 122,-14, 128,-14, 134,-12, 140,-12,
    144,-10, 148,-16, 152,-22, 154,-28, 152,-34, 148,-38,
    144,-38, 140,-36, 136,-34, 130,-32, 124,-30,
    118,-28, 115,-30, 114,-26, 114,-22,
  ]],
  ['greenland', [
    -20,70, -15,68, -18,64, -26,60, -36,58, -44,58,
    -52,60, -54,64, -52,68, -46,72, -40,76, -30,76,
    -20,74, -20,70,
  ]],
  ['japan', [
    130,32, 132,34, 136,36, 138,38, 140,40, 142,44,
    142,42, 140,38, 137,36, 135,34, 131,32, 130,32,
  ]],
  ['newGuinea', [
    131,-2, 136,-4, 140,-4, 144,-6, 148,-8, 146,-6,
    140,-4, 135,-4, 131,-2,
  ]],
];

export function createEarthCanvasTexture(): THREE.CanvasTexture {
  const W = 2048;
  const H = 1024;
  const cv = document.createElement('canvas');
  cv.width = W;
  cv.height = H;
  const c = cv.getContext('2d')!;

  // Deep dark navy ocean background — holographic style
  const ocean = c.createLinearGradient(0, 0, 0, H);
  ocean.addColorStop(0, '#020710');
  ocean.addColorStop(0.25, '#030d1c');
  ocean.addColorStop(0.5, '#041224');
  ocean.addColorStop(0.75, '#030d1c');
  ocean.addColorStop(1, '#020710');
  c.fillStyle = ocean;
  c.fillRect(0, 0, W, H);

  // Very subtle dot grid texture for holographic feel
  c.globalAlpha = 0.06;
  c.fillStyle = '#4dd9f0';
  for (let x = 0; x < W; x += 14) {
    for (let y = 0; y < H; y += 14) {
      c.beginPath();
      c.arc(x, y, 0.8, 0, Math.PI * 2);
      c.fill();
    }
  }
  c.globalAlpha = 1;

  // Subtle latitude/longitude grid — neon cyan faint
  c.strokeStyle = 'rgba(77,217,240,0.07)';
  c.lineWidth = 0.8;
  for (let lat = -75; lat <= 75; lat += 15) {
    const [, y] = p(0, lat, W, H);
    c.beginPath();
    c.moveTo(0, y);
    c.lineTo(W, y);
    c.stroke();
  }
  for (let lon = -165; lon <= 165; lon += 30) {
    const [x] = p(lon, 0, W, H);
    c.beginPath();
    c.moveTo(x, 0);
    c.lineTo(x, H);
    c.stroke();
  }

  // Draw all continents — holographic neon style
  const drawContinent = (pts: number[]) => {
    const points: [number, number][] = [];
    for (let i = 0; i < pts.length - 1; i += 2) {
      points.push(p(pts[i], pts[i + 1], W, H));
    }

    // Build path
    const buildPath = () => {
      c.beginPath();
      c.moveTo(points[0][0], points[0][1]);
      for (let i = 1; i < points.length; i++) {
        c.lineTo(points[i][0], points[i][1]);
      }
      c.closePath();
    };

    // 1. Fill with very dark translucent navy — subtle continent body
    buildPath();
    c.fillStyle = 'rgba(6, 28, 58, 0.82)';
    c.fill();

    // 2. Outer glow — wide soft blur
    c.save();
    buildPath();
    c.shadowColor = '#00e5ff';
    c.shadowBlur = 22;
    c.strokeStyle = 'rgba(0, 229, 255, 0.18)';
    c.lineWidth = 6;
    c.stroke();
    c.restore();

    // 3. Mid glow layer
    c.save();
    buildPath();
    c.shadowColor = '#4dd9f0';
    c.shadowBlur = 10;
    c.strokeStyle = 'rgba(77, 217, 240, 0.45)';
    c.lineWidth = 2.5;
    c.stroke();
    c.restore();

    // 4. Crisp bright outline on top
    c.save();
    buildPath();
    c.shadowColor = '#a0f0ff';
    c.shadowBlur = 4;
    c.strokeStyle = 'rgba(160, 240, 255, 0.92)';
    c.lineWidth = 1.2;
    c.stroke();
    c.restore();
  };

  POLYS.forEach(([, pts]) => {
    drawContinent(pts);
  });

  // Inner continent subtle highlight dots along coastlines
  c.globalAlpha = 0.22;
  POLYS.forEach(([, pts]) => {
    for (let i = 0; i < pts.length - 1; i += 6) {
      const [x, y] = p(pts[i], pts[i + 1], W, H);
      c.beginPath();
      c.arc(x, y, 1.8, 0, Math.PI * 2);
      c.fillStyle = '#7eeeff';
      c.fill();
    }
  });
  c.globalAlpha = 1;

  // Polar glow — soft blue/white at poles
  const ice1 = c.createRadialGradient(W / 2, 0, 0, W / 2, 0, H * 0.13);
  ice1.addColorStop(0, 'rgba(180,240,255,0.55)');
  ice1.addColorStop(0.5, 'rgba(100,200,240,0.18)');
  ice1.addColorStop(1, 'rgba(100,200,240,0)');
  c.fillStyle = ice1;
  c.fillRect(0, 0, W, H * 0.18);

  const ice2 = c.createRadialGradient(W / 2, H, 0, W / 2, H, H * 0.12);
  ice2.addColorStop(0, 'rgba(180,240,255,0.50)');
  ice2.addColorStop(0.5, 'rgba(100,200,240,0.15)');
  ice2.addColorStop(1, 'rgba(100,200,240,0)');
  c.fillStyle = ice2;
  c.fillRect(0, H * 0.84, W, H * 0.16);

  return new THREE.CanvasTexture(cv);
}